import { z } from "zod";
import { LessonSchema, TopicSchema } from "./Lesson";
import { Mode } from "../components/notification/NotificationContainer";
import { GeneralObject, requiredFieldMessage } from "./General";
import { QuestionBankServerResponseSchema, quizDefaultValues, QuizSettingsSchema } from "./Exam";

export const CounselorDailyItemTypes = ['', 'تست', 'مطالعه', 'رویداد', 'کوئیز', '', '', 'metadata'] as const;
export const StudentDailyItemTypes = ['لیست', 'تست', 'مطالعه', 'رویداد'] as const;

export const STATES = [
    {state: 'sending', mode: Mode.Warning, message: 'در حال ارسال برنامه'},
    {state: 'draft', mode: Mode.Error, message: 'برنامه ارسال نشده ای دارید'},
    {state: 'error', mode: Mode.Error, message: 'خطا در ارسال'},
    {state: 'sucess', mode: Mode.Success, message: 'برنامه با موفقیت ارسال شد'},
    {state: 'copying', mode: Mode.Warning, message: 'برنامه مورد نظر کپی شد'},
    {state: 'loading', mode: Mode.Warning, message: 'در حال دریافت برنامه'},
    {state: 'error-fetching', mode: Mode.Error, message: 'خطا در دریافت برنامه'},
]

export const TimeSchema = z.number().min(0);
export const TestCountSchema = z.number().min(0).max(1000);

export const WeeklyScheduleItemSchema = z.array(z.string().nullable());

export const PlanItemSchema = z.object({
    lesson: LessonSchema,
    topics: z.array(TopicSchema),
    doneTopics: z.array(TopicSchema).optional().nullable(),
    testCount: TestCountSchema,
    wrongCount: TestCountSchema,
    whiteCount: TestCountSchema,
    plannedTime: TimeSchema,
    plannedTest: TestCountSchema,
    studyType: z.number().optional().nullable()
})

export const DailyItemSchema = z.object({
    title: z.string({ required_error: requiredFieldMessage }).min(1, { message: requiredFieldMessage }),
    time: TimeSchema.optional(),
    from: TimeSchema.optional(),
    doneFrom: TimeSchema.optional().nullable(),
    to: TimeSchema.optional(),
    description: z.string().optional().nullable(),
    type: z.number().min(0).max(7),
    metadata: PlanItemSchema.optional().nullable(),
    quiz: QuizSettingsSchema.optional().nullable(),
    customColor: z.string().optional(),
    intervalCompletionType: z.number().optional().nullable(),
    role: z.number().optional()
})

export const IndexedDailyItemSchema = DailyItemSchema.extend({
    index: z.number().optional()
})

export const DayIndexedItemSchema = DailyItemSchema.extend({
    ISODay: z.string()
})

export const PlannerDatabaseItemSchema = z.record(z.string(), z.array(DailyItemSchema.nullable()));
export const IndexedPlannerDatabaseItemSchema = z.record(z.string(), z.array(IndexedDailyItemSchema.nullable()))
export const WeeklyScheduleDataSchema = z.array(WeeklyScheduleItemSchema).length(7);

export const PlannerSaveResponseSchema = z.object({
    added_score: z.number(),
    data: PlannerDatabaseItemSchema,
    link: z.string().optional().nullable()
})

export const ChronometerResponseSchema = z.object({
    lesson: LessonSchema,
    topics: z.array(TopicSchema),
    time: z.number(),
    active: z.number(),
    startTime: z.string().optional().nullable(),
    topicsLiteral: z.string().optional().nullable(),
    name: z.string().optional().nullable(),
    profilePic: z.string().optional().nullable()
})
export const ChronometerListSchema = z.array(ChronometerResponseSchema);

export const PlannerSignalsDefaultValues = { 
    testCountSignal: 0, 
    quizTimeSingal: 15,
    veryHardCount: 1,
    hardCount: 4,
    mediumCount: 3,
    easyCount: 2,
    isActive: true,
    timePickerSignal: 0,
    toTimePickerSignal: 0, 
    isFromToTimeFormat: false
}

export const DailyItemDefaultValues = { 
    title: '', 
    time: 0, 
    from: 0, 
    to: 0, 
    type: 2,
    description: '',
    customColor: '#aaaaaa',
    metadata: { 
        lesson: undefined, 
        plannedTest: 0, 
        plannedTime: 90, 
        testCount: 0, 
        whiteCount: 0, 
        wrongCount: 0, 
        topics: [],
        studyType: null
    },
    quiz: {
        isActive: true,
        time: 15,
        veryHardCount: 1,
        hardCount: 4,
        mediumCount: 3,
        easyCount: 2
    }
}

export const WeeklyScheduleDefaultValues = [
    ['', '', '', '', '', '', ''],
    ['', '', '', '', '', '', ''],
    ['', '', '', '', '', '', ''],
    ['', '', '', '', '', '', ''],
    ['', '', '', '', '', '', ''],
    ['', '', '', '', '', '', ''],
    ['', '', '', '', '', '', ''],
]

export type PlanItemType = z.infer<typeof PlanItemSchema>;
export type QuizSettingsType = z.infer<typeof QuizSettingsSchema>;
export type DailyItemType = z.infer<typeof DailyItemSchema>;
export type DayIndexedItemType = z.infer<typeof DayIndexedItemSchema>;
export type IndexedDailyItemType = z.infer<typeof IndexedDailyItemSchema>;
export type PlannerDatabaseType = z.infer<typeof PlannerDatabaseItemSchema>;
export type WeeklyScheduleDataType = z.infer<typeof WeeklyScheduleDataSchema>;
export type PlannerSaveResponseType = z.infer<typeof PlannerSaveResponseSchema>;
export type QuestionBankServerResponseType = z.infer<typeof QuestionBankServerResponseSchema>;
export type ChronometerResponseType = z.infer<typeof ChronometerResponseSchema>;
export type ChronometerListType = z.infer<typeof ChronometerListSchema>;

export type StateType = {
    state: string;
    message: string;
    mode: Mode
}

export type SummationDataType = {
    testSum: number;
    timeSum: number;
    dailyBasedTestSum: GeneralObject<number>;
    dailyBasedTimeSum: GeneralObject<number>;
    lessonBasedTestSum: GeneralObject<number>;
    lessonBasedTimeSum: GeneralObject<number>;
    dailyBasedStudentTestSum: GeneralObject<number>;
    dailyBasedStudentTimeSum: GeneralObject<number>;
    dailyBasedStudentPlannedTestSum: GeneralObject<number>;
    dailyBasedStudentPlannedTimeSum: GeneralObject<number>;
}

export type CompletionDataType = {
    percentage: number;
    plannedTest: number | null | undefined;
    plannedTime: string | null;
    completedTime: string | null;
    completedTest: number | null | undefined;
    type: number | null | undefined;
}

export type PlannerRecieverType = {
    receiverRole: number;
    receiverId: string;
    senderRole: number;
    senderId: string;
    submitOnChange: boolean;
}